import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Car, Calendar, Gauge, Wrench, Fuel, Package, Image as ImageIcon } from 'lucide-react';
import { getApiUrl } from '../utils/config';
import Logo from './Logo';

const API = getApiUrl();

const PublicCarView = () => {
  // Extract token from URL manually since we're not using React Router's Route component
  const shareToken = window.location.pathname.split('/public-car/')[1];
  const [carData, setCarData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (shareToken) {
      fetchPublicCar();
    } else {
      setError('Invalid share link');
      setIsLoading(false);
    }
  }, [shareToken]);

  const fetchPublicCar = async () => {
    try {
      const response = await axios.get(`${API}/public/car/${shareToken}`);
      setCarData(response.data);
    } catch (error) {
      console.error('Error fetching public car:', error);
      setError(error.response?.data?.detail || 'Failed to load car information');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading car information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Link Not Found</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-8" />
            <span className="text-xl font-bold text-gray-800">Track My Garage</span>
          </div>
          <div className="text-sm text-gray-600">Shared Vehicle</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Hero Section with Car Info and Main Photo */}
        <Card className="overflow-hidden">
          {/* Main Photo Hero */}
          {carData.photos && carData.photos.length > 0 ? (
            <div className="relative h-[400px] md:h-[500px] overflow-hidden">
              <img 
                src={carData.photos[0].url} 
                alt="Main vehicle photo"
                className="w-full h-full object-cover"
              />
              {/* Overlay gradient for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              
              {/* Car Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-12 h-12" />
                  <div>
                    <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg">
                      {carData.year && <span>{carData.year} </span>}
                      {carData.make && <span>{carData.make} </span>}
                      {carData.model && <span>{carData.model}</span>}
                      {!carData.make && !carData.model && 'Vehicle Information'}
                    </h1>
                    {carData.current_odometer && (
                      <div className="flex items-center gap-2 mt-2">
                        <Gauge className="w-5 h-5" />
                        <span className="text-lg font-medium drop-shadow">{carData.current_odometer.toLocaleString()} km</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Fallback if no photos - gradient header
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 md:p-12 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Car className="w-12 h-12" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold">
                    {carData.year && <span>{carData.year} </span>}
                    {carData.make && <span>{carData.make} </span>}
                    {carData.model && <span>{carData.model}</span>}
                    {!carData.make && !carData.model && 'Vehicle Information'}
                  </h1>
                  {carData.current_odometer && (
                    <div className="flex items-center gap-2 mt-2 text-blue-100">
                      <Gauge className="w-5 h-5" />
                      <span className="text-lg">{carData.current_odometer.toLocaleString()} km</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Additional Photos Gallery */}
          {carData.photos && carData.photos.length > 1 && (
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                More Photos
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {carData.photos.slice(1).map((photo, index) => (
                  <div key={index} className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border hover:shadow-lg transition-shadow cursor-pointer">
                    <img 
                      src={photo.url} 
                      alt={`Car photo ${index + 2}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Service History */}
        {carData.service_records && carData.service_records.length > 0 && (
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-6 h-6 text-blue-600" />
                Service History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {carData.service_records.map((record, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">
                          {new Date(record.service_date).toLocaleDateString()}
                        </span>
                      </div>
                      {record.total_cost && (
                        <span className="text-green-600 font-semibold">
                          ${record.total_cost.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      {record.odometer_reading && (
                        <div>Odometer: {record.odometer_reading.toLocaleString()} km</div>
                      )}
                      {record.tasks && record.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} className="ml-4">
                          • {task.task_type}: {task.description}
                        </div>
                      ))}
                      {record.location && <div>Location: {record.location}</div>}
                      {record.performed_by && <div>Performed by: {record.performed_by}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fuel Economy */}
        {carData.avg_fuel_efficiency && (
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="flex items-center gap-2">
                <Fuel className="w-6 h-6 text-green-600" />
                Fuel Economy
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {carData.avg_fuel_efficiency.toFixed(1)}
                </div>
                <div className="text-gray-600">Average L/100km</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parts List */}
        {carData.parts && carData.parts.length > 0 && (
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-6 h-6 text-purple-600" />
                Installed Parts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {carData.parts.map((part, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <h3 className="font-semibold mb-2">{part.part_name}</h3>
                    {part.brand && <div className="text-sm text-gray-600">Brand: {part.brand}</div>}
                    {part.part_number && <div className="text-sm text-gray-600">Part #: {part.part_number}</div>}
                    {part.installation_date && (
                      <div className="text-sm text-gray-600">
                        Installed: {new Date(part.installation_date).toLocaleDateString()}
                      </div>
                    )}
                    {part.photos && part.photos.length > 0 && (
                      <div className="mt-2 flex gap-2">
                        {part.photos.map((photo, photoIndex) => (
                          <img 
                            key={photoIndex}
                            src={photo.url}
                            alt={`Part photo ${photoIndex + 1}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-gray-600">
          <p className="mb-2">Powered by Track My Garage</p>
          <p className="text-sm">Want to manage your own vehicles? Visit us to get started!</p>
        </div>
      </div>
    </div>
  );
};

export default PublicCarView;
